// src/components/OtpGate.js
// Full-screen block shown while a signup or new-device OTP is outstanding.
// Nothing behind it is reachable until the code is verified — this is the
// actual access gate, not just a suggestion.
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck } from 'lucide-react';

const INK = '#1e1b4b';
const MUTE = '#6b6892';
const GOLD = '#f97316';
const CORAL = '#ff6b4a';
const CREAM = '#fff8f0';

const COPY = {
  signup: {
    title: 'Verify your email',
    body: 'Enter the 6-digit code we just sent to confirm this is really you.',
  },
  'new-device': {
    title: "We don't recognize this device",
    body: "For your account's safety, enter the 6-digit code we sent to confirm this sign-in.",
  },
};

export default function OtpGate() {
  const { otpRequired, verifyGateOtp, resendGateOtp, logout } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const [wrongCount, setWrongCount] = useState(0);

  if (!otpRequired) return null;
  const copy = COPY[otpRequired.purpose] || COPY.signup;
  const locked = wrongCount >= 5;

  async function submit() {
    if (code.trim().length !== 6) { setError('Enter all 6 digits'); return; }
    setLoading(true); setError('');
    const ok = await verifyGateOtp(code.trim());
    setLoading(false);
    if (!ok) {
      setWrongCount(w => w + 1);
      setError(locked ? 'Too many wrong attempts — request a new code.' : "That code doesn't match — try again.");
      setCode('');
    }
  }

  async function resend() {
    setLoading(true); setError('');
    await resendGateOtp();
    setWrongCount(0);
    setCode('');
    setLoading(false);
    setResent(true);
    setTimeout(() => setResent(false), 2500);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, background: CREAM, color: INK,
      fontFamily: 'Nunito, sans-serif', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center',
    }}>
      <ShieldCheck size={32} color={GOLD} style={{ marginBottom: 16 }} />
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{copy.title}</h1>
      <p style={{ color: MUTE, fontSize: 13.5, maxWidth: 360, marginBottom: 8, lineHeight: 1.55 }}>{copy.body}</p>
      <p style={{ color: MUTE, fontSize: 12, marginBottom: 22 }}>Sent to <b style={{ color: INK }}>{otpRequired.targetEmail}</b></p>

      <input
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="••••••"
        inputMode="numeric"
        disabled={locked}
        style={{
          width: 180, textAlign: 'center', letterSpacing: 8, fontSize: 22, fontWeight: 800,
          padding: '14px 12px', borderRadius: 10, border: `1.5px solid ${error ? '#c0392b' : 'rgba(30,27,75,0.15)'}`,
          background: '#fff', color: INK, outline: 'none', marginBottom: 12, fontFamily: 'inherit',
        }}
      />
      {error && <p style={{ fontSize: 12, color: '#c0392b', marginBottom: 12 }}>{error}</p>}

      {!locked ? (
        <button
          onClick={submit}
          disabled={loading || code.length !== 6}
          style={{
            padding: '12px 28px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg, ${GOLD}, ${CORAL})`, color: '#fff',
            fontWeight: 700, fontSize: 14, marginBottom: 14,
          }}
        >
          {loading ? 'Checking…' : 'Verify'}
        </button>
      ) : null}

      <div style={{ display: 'flex', gap: 16, fontSize: 12.5 }}>
        <button onClick={resend} disabled={loading} style={{ background: 'none', border: 'none', color: GOLD, fontWeight: 700, cursor: 'pointer' }}>
          {resent ? 'New code sent' : 'Resend code'}
        </button>
        <button onClick={logout} style={{ background: 'none', border: 'none', color: MUTE, cursor: 'pointer' }}>
          Sign out
        </button>
      </div>
    </div>
  );
}
